import { useGetOrder } from 'ClientPortal/src/api/Queries/orders'
import { useGetAppraiserUsers, useGetCustomerUsers } from 'ClientPortal/src/api/Queries/users'
import { useGetAsset } from 'ClientPortal/src/api/Queries/assets'
import { useValueCheckDeviationAnalysis } from 'ClientPortal/src/pages/Assets/AssetDetails/ValueCheck/ValueCheckLayout/useValueCheckDeviationAnalysis'
import { useDownloadReport, useGetValuation } from 'ClientPortal/src/api/Queries/valuations'
import { useTypedParams } from 'ClientPortal/src/router/useTypedParams'
import { throwOnForbiddenApiError } from 'ClientPortal/src/api/react-query-config'
import { assembleAssetTitle, ValuationDeviationOption, ValuationDeviationOptions } from 'ComponentLibrary'
import invariant from 'tiny-invariant'
import { useGetValueCheck } from 'ClientPortal/src/api/Queries/value-checks'
import { useTranslation } from 'react-i18next'

export function useValueCheckPageLayout() {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { assetId, valueCheckId } = useTypedParams(['assetId', 'valueCheckId'])
    const { valueCheck, isValueCheckPending } = useGetValueCheck({ valueCheckId, throwOnError: throwOnForbiddenApiError })
    const { asset, isAssetPending } = useGetAsset({ assetId, throwOnError: throwOnForbiddenApiError })
    const { order, isOrderInitialLoading } = useGetOrder({ orderId: valueCheck?.order_uuid })
    const { valuation: baseValuation } = useGetValuation({
        valuationId: valueCheck?.base_valuation_uuid,
        throwOnError: throwOnForbiddenApiError,
    })
    const { downloadReport, isDownloadingReport } = useDownloadReport(assetId)
    const { customerUsers, isCustomerUsersPending } = useGetCustomerUsers({
        orderId: valueCheck?.order_uuid,
        enabled: !!valueCheck?.order_uuid,
    })
    const { appraiserUsers, isAppraiserUsersPending } = useGetAppraiserUsers({
        orderId: valueCheck?.order_uuid,
        enabled: !!valueCheck?.order_uuid,
    })
    const { appraiserUsers: baseValuationAppraiserUsers } = useGetAppraiserUsers({
        orderId: baseValuation?.order_uuid,
        enabled: !!baseValuation?.order_uuid,
    })
    const { deviationAnalysis, isDeviationAnalysisPending } = useValueCheckDeviationAnalysis({
        valueCheckId: valueCheck?.uuid,
        comparisonValuationId: valueCheck?.base_valuation_uuid,
    })
    const assignedCustomerUsers =
        order?.assigned_customer_users
            .map((customerUserId) => customerUsers.find((customerUser) => customerUser.id === customerUserId))
            .filter((customerUser) => customerUser !== undefined) ?? []

    const comparisonValuationOption: ValuationDeviationOption | undefined = valueCheck?.base_valuation_uuid
        ? (() => {
              if (baseValuation) {
                  invariant(!baseValuation.is_draft, 'value check base valuation must be finalised')
              }
              return {
                  value: valueCheck.base_valuation_uuid,
                  title: tCommon('valuationReport'),
                  date: baseValuation?.valuation_date,
                  valuationDate: baseValuation?.valuation_date,
                  status: baseValuation?.status,
                  isDraft: baseValuation?.is_draft,
              }
          })()
        : undefined

    const baseValuationOption: ValuationDeviationOption | undefined = valueCheck?.uuid
        ? (() => {
              return {
                  value: valueCheck.uuid,
                  title: tCommon('valueCheck'),
                  date: valueCheck.valuation_date,
                  valuationDate: valueCheck.valuation_date,
              }
          })()
        : undefined

    const valuationOptions: ValuationDeviationOptions = [
        ...(comparisonValuationOption ? [comparisonValuationOption] : []),
        ...(baseValuationOption ? [baseValuationOption] : []),
    ]

    const assetTitle = assembleAssetTitle({
        ...asset?.address,
        customer_asset_reference: asset?.customer_asset_reference,
    })

    return {
        valueCheck,
        baseValuation,
        asset,
        assetId,
        assignedCustomerUsers,
        order,
        isOrderInitialLoading,
        isCustomerUsersPending,
        isValueCheckPageLayoutPending: isValueCheckPending || isAssetPending,
        appraiserUsers,
        isAppraiserUsersPending,
        baseValuationAppraiserUsers,
        downloadReport,
        isDownloadingReport,
        valuationOptions,
        baseValuationOption,
        comparisonValuationOption,
        deviationAnalysis,
        isDeviationAnalysisPending,
        assetTitle,
    }
}
